import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';

export interface SeoData {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  locale?: string;
  alternateLocale?: string;
  alternateUrl?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private readonly baseUrl = 'https://shumoul.com'; // Update with your actual domain
  private readonly defaultImage = 'https://shumoul.com/assets/images/logo.svg';
  private readonly siteName = 'Shumoul';

  constructor(
    private meta: Meta,
    private title: Title,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router
  ) {}

  updateSeoMeta(data: SeoData): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const currentUrl = this.router.url;
    const fullUrl = `${this.baseUrl}${currentUrl}`;
    const lang = currentUrl.startsWith('/ar') ? 'ar' : 'en';
    
    // Update HTML lang attribute
    this.document.documentElement.lang = lang;
    const alternateLang = lang === 'ar' ? 'en' : 'ar';
    
    // Build alternate URL
    let alternateUrl = currentUrl;
    if (currentUrl.startsWith('/ar')) {
      alternateUrl = currentUrl.replace('/ar', '/en');
    } else if (currentUrl.startsWith('/en')) {
      alternateUrl = currentUrl.replace('/en', '/ar');
    } else {
      alternateUrl = `/${alternateLang}${currentUrl}`;
    }
    const fullAlternateUrl = `${this.baseUrl}${alternateUrl}`;

    // Set title
    const title = data.title || 'Shumoul - Cloud Business Management Solutions';
    this.title.setTitle(title);

    // Basic Meta Tags
    this.meta.updateTag({ name: 'description', content: data.description || 'Shumoul provides comprehensive cloud-based business management solutions including accounting, inventory, sales, POS, restaurant management, and more.' });
    this.meta.updateTag({ name: 'keywords', content: data.keywords || 'shumoul, business management, cloud solutions, accounting software, inventory management, POS system, restaurant management, ERP, Saudi Arabia' });
    this.meta.updateTag({ name: 'author', content: data.author || 'Shumoul' });
    this.meta.updateTag({ name: 'robots', content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' });
    this.meta.updateTag({ name: 'googlebot', content: 'index, follow' });
    this.meta.updateTag({ name: 'language', content: lang });
    this.meta.updateTag({ name: 'revisit-after', content: '7 days' });

    // Open Graph Tags
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: data.description || 'Shumoul provides comprehensive cloud-based business management solutions' });
    this.meta.updateTag({ property: 'og:image', content: data.image || this.defaultImage });
    this.meta.updateTag({ property: 'og:url', content: data.url || fullUrl });
    this.meta.updateTag({ property: 'og:type', content: data.type || 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: this.siteName });
    this.meta.updateTag({ property: 'og:locale', content: lang === 'ar' ? 'ar_SA' : 'en_US' });
    this.meta.updateTag({ property: 'og:locale:alternate', content: alternateLang === 'ar' ? 'ar_SA' : 'en_US' });

    // Twitter Card Tags
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: title });
    this.meta.updateTag({ name: 'twitter:description', content: data.description || 'Shumoul provides comprehensive cloud-based business management solutions' });
    this.meta.updateTag({ name: 'twitter:image', content: data.image || this.defaultImage });

    // Canonical URL
    this.updateCanonicalUrl(data.url || fullUrl);

    // Hreflang Tags
    this.updateHreflangTags(fullUrl, fullAlternateUrl, lang, alternateLang);

    // Structured Data (JSON-LD)
    this.updateStructuredData(data, fullUrl, lang);
  }

  private updateCanonicalUrl(url: string): void {
    let link: HTMLLinkElement = this.document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private updateHreflangTags(currentUrl: string, alternateUrl: string, currentLang: string, alternateLang: string): void {
    // Remove existing hreflang tags
    const existingHreflangs = this.document.querySelectorAll('link[rel="alternate"][hreflang]');
    existingHreflangs.forEach(tag => tag.remove());

    // Add current language hreflang
    let linkCurrent = this.document.createElement('link');
    linkCurrent.setAttribute('rel', 'alternate');
    linkCurrent.setAttribute('hreflang', currentLang);
    linkCurrent.setAttribute('href', currentUrl);
    this.document.head.appendChild(linkCurrent);

    // Add alternate language hreflang
    let linkAlternate = this.document.createElement('link');
    linkAlternate.setAttribute('rel', 'alternate');
    linkAlternate.setAttribute('hreflang', alternateLang);
    linkAlternate.setAttribute('href', alternateUrl);
    this.document.head.appendChild(linkAlternate);

    // Add x-default hreflang (usually points to English)
    let linkDefault = this.document.createElement('link');
    linkDefault.setAttribute('rel', 'alternate');
    linkDefault.setAttribute('hreflang', 'x-default');
    linkDefault.setAttribute('href', alternateLang === 'en' ? alternateUrl : currentUrl);
    this.document.head.appendChild(linkDefault);
  }

  private updateStructuredData(data: SeoData, url: string, lang: string): void {
    // Remove existing structured data
    const existingScript = this.document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': data.type === 'article' ? 'Article' : 'WebSite',
      name: this.siteName,
      url: this.baseUrl,
      description: data.description || 'Shumoul provides comprehensive cloud-based business management solutions',
      inLanguage: lang === 'ar' ? 'ar-SA' : 'en-US',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${this.baseUrl}/search?q={search_term_string}`
        },
        'query-input': 'required name=search_term_string'
      }
    };

    if (data.type === 'article') {
      Object.assign(structuredData, {
        headline: data.title,
        image: data.image || this.defaultImage,
        datePublished: data.publishedTime,
        dateModified: data.modifiedTime || data.publishedTime,
        author: {
          '@type': 'Organization',
          name: 'Shumoul'
        },
        publisher: {
          '@type': 'Organization',
          name: 'Shumoul',
          logo: {
            '@type': 'ImageObject',
            url: this.defaultImage
          }
        }
      });
    } else {
      Object.assign(structuredData, {
        '@type': 'Organization',
        name: 'Shumoul',
        logo: this.defaultImage,
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+966920032013',
          contactType: 'customer service',
          areaServed: 'SA',
          availableLanguage: ['Arabic', 'English']
        },
        sameAs: [
          'https://www.tiktok.com/@shumoulalibtiqar',
          'https://www.linkedin.com/in/shumoul-alibtiqar-3bb4a6360/',
          'https://www.snapchat.com/add/shumoulalibtiqar',
          'https://www.instagram.com/shumoulalibtiqar/',
          'https://www.facebook.com/Shumoulallibtiqar/',
          'https://www.youtube.com/@ShumulAlIbtiqarIT'
        ]
      });

      // Add SoftwareApplication schema for ERP platform
      const softwareSchema = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Shumoul ERP',
        operatingSystem: 'Web',
        applicationCategory: 'BusinessApplication',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'SAR'
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          ratingCount: '1250'
        }
      };

      const softwareScript = this.document.createElement('script');
      softwareScript.type = 'application/ld+json';
      softwareScript.text = JSON.stringify(softwareSchema);
      this.document.head.appendChild(softwareScript);
    }

    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    this.document.head.appendChild(script);
  }

  // Helper method to get SEO data based on route
  getSeoDataForRoute(route: string): SeoData {
    const routeData: { [key: string]: SeoData } = {
      '/ar': {
        title: 'شمول - حلول إدارة الأعمال السحابية الشاملة',
        description: 'شمول يوفر حلول إدارة أعمال سحابية شاملة تشمل المحاسبة، المخزون، المبيعات، نقاط البيع، إدارة المطاعم والمزيد.',
        keywords: 'شمول, إدارة الأعمال, حلول سحابية, برنامج محاسبة, إدارة المخزون, نظام نقاط البيع, إدارة المطاعم, ERP, السعودية',
        locale: 'ar_SA'
      },
      '/en': {
        title: 'Shumoul - Comprehensive Cloud Business Management Solutions',
        description: 'Shumoul provides comprehensive cloud-based business management solutions including accounting, inventory, sales, POS, restaurant management, and more.',
        keywords: 'shumoul, business management, cloud solutions, accounting software, inventory management, POS system, restaurant management, ERP, Saudi Arabia',
        locale: 'en_US'
      },
      '/ar/about': {
        title: 'عن شمول - من نحن',
        description: 'تعرف على شمول، منصة إدارة الأعمال السحابية الرائدة في المنطقة.',
        keywords: 'عن شمول, من نحن, شركة شمول, إدارة الأعمال',
        locale: 'ar_SA'
      },
      '/en/about': {
        title: 'About Shumoul - Who We Are',
        description: 'Learn about Shumoul, the leading cloud business management platform in the region.',
        keywords: 'about shumoul, who we are, shumoul company, business management',
        locale: 'en_US'
      },
      '/ar/systems': {
        title: 'أنظمة شمول - حلول متكاملة لإدارة الأعمال',
        description: 'استكشف أنظمة شمول المتكاملة: المحاسبة، المخزون، المبيعات، نقاط البيع، المطاعم والمزيد.',
        keywords: 'أنظمة شمول, نظام محاسبة, نظام مخزون, نظام مبيعات, نظام نقاط البيع, نظام مطاعم',
        locale: 'ar_SA'
      },
      '/en/systems': {
        title: 'Shumoul Systems - Integrated Business Management Solutions',
        description: 'Explore Shumoul integrated systems: Accounting, Inventory, Sales, POS, Restaurant and more.',
        keywords: 'shumoul systems, accounting system, inventory system, sales system, POS system, restaurant system',
        locale: 'en_US'
      },
      '/ar/prices': {
        title: 'أسعار شمول - خطط وأسعار مناسبة',
        description: 'اكتشف خطط وأسعار شمول المناسبة لعملك. حلول سحابية بأسعار تنافسية.',
        keywords: 'أسعار شمول, خطط شمول, أسعار برامج إدارة الأعمال, اشتراك شمول',
        locale: 'ar_SA'
      },
      '/en/prices': {
        title: 'Shumoul Pricing - Affordable Plans and Prices',
        description: 'Discover Shumoul plans and prices suitable for your business. Competitive cloud solutions.',
        keywords: 'shumoul pricing, shumoul plans, business management software prices, shumoul subscription',
        locale: 'en_US'
      },
      '/ar/contact': {
        title: 'اتصل بنا - شمول',
        description: 'تواصل مع فريق شمول. نحن هنا لمساعدتك في جميع استفساراتك.',
        keywords: 'اتصل بنا, تواصل مع شمول, دعم العملاء, خدمة العملاء',
        locale: 'ar_SA'
      },
      '/en/contact': {
        title: 'Contact Us - Shumoul',
        description: 'Get in touch with Shumoul team. We are here to help with all your inquiries.',
        keywords: 'contact us, contact shumoul, customer support, customer service',
        locale: 'en_US'
      },
      // Systems Sub-pages - Arabic
      '/ar/systems/account': {
        title: 'نظام المحاسبة السحابي - شمول',
        description: 'نظام محاسبي سحابي متكامل يسهل إدارة القيود اليومية، التقارير الختامية، والموازنات.',
        keywords: 'برنامج محاسبة, محاسبة سحابية, شمول, نظام مالي',
        locale: 'ar_SA'
      },
      '/ar/systems/warehouse': {
        title: 'نظام إدارة المستودعات والمخازن - شمول',
        description: 'إدارة شاملة للمخزون، التحويلات المخزنية، والجرد بدقة مع نظام شمول.',
        keywords: 'إدارة مستودعات, إدارة مخزون, شمول, نظام مخازن',
        locale: 'ar_SA'
      },
      '/ar/systems/purchase': {
        title: 'نظام إدارة المشتريات - شمول',
        description: 'أتمتة عمليات الشراء وإدارة الموردين والمخزون بكفاءة عالية.',
        keywords: 'إدارة مشتريات, موردين, شمول, أتمتة المشتريات',
        locale: 'ar_SA'
      },
      '/ar/systems/sales': {
        title: 'نظام إدارة المبيعات - شمول',
        description: 'تابع مبيعاتك وعروض الأسعار وأوامر البيع في مكان واحد وبسهولة تامة.',
        keywords: 'إدارة مبيعات, عروض أسعار, شمول, أوامر بيع',
        locale: 'ar_SA'
      },
      '/ar/systems/pos': {
        title: 'نظام نقاط البيع POS - شمول',
        description: 'نظام نقاط بيع سريع وسهل الاستخدام للمتاجر والمحلات التجارية والمطاعم.',
        keywords: 'نقاط بيع, POS, شمول, نظام كاشير',
        locale: 'ar_SA'
      },
      '/ar/systems/resturant': {
        title: 'نظام إدارة المطاعم والكافيهات - شمول',
        description: 'حلول متكاملة لإدارة المطاعم، من الطلبات إلى تقارير المبيعات ورضا العملاء.',
        keywords: 'إدارة مطاعم, نظام مطاعم, شمول, كافيهات',
        locale: 'ar_SA'
      },
      '/ar/systems/finance': {
        title: 'نظام الإدارة المالية - شمول',
        description: 'تحكم كامل في مواردك المالية وتدفقاتك النقدية مع نظام الإدارة المالية من شمول.',
        keywords: 'إدارة مالية, تدفقات نقدية, شمول, محاسبة مالية',
        locale: 'ar_SA'
      },
      '/ar/systems/ecommerce': {
        title: 'منصة التجارة الإلكترونية - شمول',
        description: 'أنشئ متجرك الإلكتروني المتكامل واربطه مع جميع أنظمة شمول لإدارة مخزونك ومبيعاتك.',
        keywords: 'تجارة إلكترونية, متجر إلكتروني, شمول, بيع أونلاين',
        locale: 'ar_SA'
      },
      '/ar/systems/attendance': {
        title: 'نظام الحضور والانصراف - شمول',
        description: 'تتبع حضور وانصراف الموظفين بدقة مع نظام شمول المتكامل، يدعم مختلف أنواع أجهزة البصمة.',
        keywords: 'حضور وانصراف, إدارة موظفين, شمول, تتبع وقت',
        locale: 'ar_SA'
      },
      '/ar/systems/distribution': {
        title: 'نظام التوزيع واللوجستيات - شمول',
        description: 'إدارة عمليات التوزيع واللوجستيات وحركة الأسطول بكفاءة عالية مع شمول.',
        keywords: 'نظام توزيع, لوجستيات, شمول, إدارة أسطول',
        locale: 'ar_SA'
      },
      '/ar/systems/crm': {
        title: 'نظام إدارة علاقات العملاء CRM - شمول',
        description: 'حسن علاقتك مع عملائك وزد مبيعاتك مع نظام شمول لإدارة علاقات العملاء المتطور.',
        keywords: 'CRM, إدارة عملاء, شمول, علاقات العملاء',
        locale: 'ar_SA'
      },
      '/ar/systems/projectManagement': {
        title: 'نظام إدارة المشاريع - شمول',
        description: 'خطط ونفذ مشاريعك بدقة واحترافية وتابع الإنجاز مع نظام شمول.',
        keywords: 'إدارة مشاريع, شمول, تخطيط مشاريع',
        locale: 'ar_SA'
      },
      '/ar/systems/sports': {
        title: 'نظام إدارة الأندية الرياضية - شمول',
        description: 'نظام متخصص لإدارة العضويات والاشتراكات والمدربين في الأندية الرياضية.',
        keywords: 'إدارة أندية, أندية رياضية, شمول, اشتراكات',
        locale: 'ar_SA'
      },
      '/ar/systems/support': {
        title: 'نظام خدمة العملاء والتذاكر - شمول',
        description: 'قدم خدمة دعم فني استثنائية لعملائك مع نظام التذاكر المتكامل والمتابعة اللحظية.',
        keywords: 'دعم فني, نظام تذاكر, شمول, خدمة عملاء',
        locale: 'ar_SA'
      },
      '/ar/systems/parking': {
        title: 'نظام إدارة المواقف - شمول',
        description: 'حلول ذكية لإدارة مواقف السيارات بدقة وسهولة مع نظام شمول.',
        keywords: 'إدارة مواقف, مواقف سيارات, شمول, حلول ذكية',
        locale: 'ar_SA'
      },
      // Systems Sub-pages - English
      '/en/systems/account': {
        title: 'Cloud Accounting System - Shumoul',
        description: 'Integrated cloud accounting system that facilitates managing daily journals, final reports, and budgets.',
        keywords: 'accounting software, cloud accounting, shumoul, financial system',
        locale: 'en_US'
      },
      '/en/systems/warehouse': {
        title: 'Warehouse Management System - Shumoul',
        description: 'Comprehensive inventory management, stock transfers, and accurate auditing with Shumoul.',
        keywords: 'warehouse management, inventory management, shumoul',
        locale: 'en_US'
      },
      '/en/systems/purchase': {
        title: 'Purchase Management System - Shumoul',
        description: 'Automate purchasing processes and manage suppliers and inventory efficiently.',
        keywords: 'purchase management, shumoul, supplier management',
        locale: 'en_US'
      },
      '/en/systems/sales': {
        title: 'Sales Management System - Shumoul',
        description: 'Track your sales, quotes, and sales orders in one place easily.',
        keywords: 'sales management, shumoul, sales orders',
        locale: 'en_US'
      },
      '/en/systems/pos': {
        title: 'POS System - Shumoul',
        description: 'Fast and easy-to-use point of sale system for stores, retail shops and restaurants.',
        keywords: 'point of sale, POS, shumoul, cashier system',
        locale: 'en_US'
      },
      '/en/systems/resturant': {
        title: 'Restaurant Management System - Shumoul',
        description: 'Integrated solutions for managing restaurants, from orders to sales reports and customer satisfaction.',
        keywords: 'restaurant management, restaurant system, shumoul',
        locale: 'en_US'
      },
      '/en/systems/finance': {
        title: 'Financial Management System - Shumoul',
        description: 'Full control over your financial resources and cash flows with Shumoul.',
        keywords: 'financial management, shumoul, cash flow',
        locale: 'en_US'
      },
      '/en/systems/ecommerce': {
        title: 'E-commerce Platform - Shumoul',
        description: 'Create your integrated online store and link it with all Shumoul systems.',
        keywords: 'e-commerce, online store, shumoul',
        locale: 'en_US'
      },
      '/en/systems/attendance': {
        title: 'Attendance System - Shumoul',
        description: 'Accurately track employee attendance and departure with Shumoul integrated system.',
        keywords: 'attendance, employee management, shumoul',
        locale: 'en_US'
      },
      '/en/systems/distribution': {
        title: 'Distribution and Logistics System - Shumoul',
        description: 'Manage distribution and logistics operations with high efficiency with Shumoul.',
        keywords: 'distribution system, logistics, shumoul',
        locale: 'en_US'
      },
      '/en/systems/crm': {
        title: 'CRM System - Shumoul',
        description: 'Improve your customer relationships and increase sales with Shumoul CRM.',
        keywords: 'CRM, customer management, shumoul',
        locale: 'en_US'
      },
      '/en/systems/projectManagement': {
        title: 'Project Management System - Shumoul',
        description: 'Plan and execute your projects accurately and professionally with Shumoul System.',
        keywords: 'project management, shumoul, planning',
        locale: 'en_US'
      },
      '/en/systems/sports': {
        title: 'Sports Club Management System - Shumoul',
        description: 'Specialized system for managing memberships and subscriptions in sports clubs.',
        keywords: 'club management, shumoul, sports',
        locale: 'en_US'
      },
      '/en/systems/support': {
        title: 'Customer Service and Ticketing System - Shumoul',
        description: 'Provide exceptional technical support to your customers with the integrated ticketing system.',
        keywords: 'technical support, ticketing system, shumoul',
        locale: 'en_US'
      },
      '/en/systems/parking': {
        title: 'Parking Management System - Shumoul',
        description: 'Smart solutions for managing car parks accurately and easily with Shumoul.',
        keywords: 'parking management, shumoul, smart parking',
        locale: 'en_US'
      },
      // Other Pages - Arabic
      '/ar/blog': {
        title: 'مدونة شمول - مقالات وأخبار تقنية المبيعات والمحاسبة',
        description: 'اقرأ أحدث المقالات والنصائح حول إدارة الأعمال، المحاسبة، والتقنيات الحديثة في شمول.',
        keywords: 'مدونة شمول, نصائح محاسبية, إدارة أعمال, مقالات تقنية',
        locale: 'ar_SA'
      },
      '/ar/news': {
        title: 'أخبار شمول - آخر التطورات والتحديثات',
        description: 'تابع آخر أخبار شركة شمول، التحديثات الجديدة، والفعاليات التي نشارك بها.',
        keywords: 'أخبار شمول, تحديثات شمول, فعاليات',
        locale: 'ar_SA'
      },
      '/ar/commonquestions': {
        title: 'الأسئلة الشائعة - شمول',
        description: 'إجابات على أكثر الأسئلة شيوعاً حول أنظمة شمول وكيفية استخدامها.',
        keywords: 'الأسئلة الشائعة, FAQ, مساعدة شمول',
        locale: 'ar_SA'
      },
      '/ar/zatcafatoora': {
        title: 'الفاتورة الإلكترونية - شمول و زكاة ودخل',
        description: 'تعرف على حلول شمول المعتمدة للفاتورة الإلكترونية المتوافقة مع متطلبات هيئة الزكاة والضريبة والجمارك.',
        keywords: 'الفاتورة الإلكترونية, فاتورة, زكاة ودخل, متطلبات الهيئة, شمول',
        locale: 'ar_SA'
      },
      '/ar/technicalsupport': {
        title: 'الدعم الفني - شمول',
        description: 'فريق الدعم الفني في شمول جاهز لمساعدتك في حل أي مشكلات تقنية تواجهك.',
        keywords: 'دعم فني, مساعدة تقنية, تواصل معنا',
        locale: 'ar_SA'
      },
      '/ar/kds': {
        title: 'نظام شاشات المطبخ KDS - شمول',
        description: 'نظام شاشات المطبخ المتطور لتنظيم الطلبات وتحسين سرعة التحضير في المطاعم.',
        keywords: 'KDS, شاشات مطبخ, نظام مطاعم, شمول',
        locale: 'ar_SA'
      },
      '/ar/support': {
        title: 'مركز المساعدة والدعم - شمول',
        description: 'مركز المساعدة الشامل لأنظمة شمول، شروحات ودعم فني متكامل.',
        keywords: 'دعم, مساعدة, مركز الدعم, شمول',
        locale: 'ar_SA'
      },
      '/ar/requestatrialversion': {
        title: 'اطلب نسخة تجريبية - شمول',
        description: 'جرب أنظمة شمول مجاناً واكتشف كيف يمكننا مساعدتك في تطوير عملك.',
        keywords: 'نسخة تجريبية, تجربة مجانية, شمول',
        locale: 'ar_SA'
      },
      '/ar/youtubevideos': {
        title: 'دروس الفيديو - شمول',
        description: 'شروحات فيديو مفصلة حول كيفية استخدام وتخصيص أنظمة شمول المختلفة.',
        keywords: 'دروس فيديو, شرح شمول, يوتيوب شمول',
        locale: 'ar_SA'
      },
      '/ar/trainingcenter': {
        title: 'مركز التدريب - شمول',
        description: 'برامج تدريبية متكاملة لمساعدتك وموظفيك على إتقان استخدام أنظمة شمول.',
        keywords: 'تدريب شمول, مركز تدريب, دورات محاسبية',
        locale: 'ar_SA'
      },
      '/ar/complaints': {
        title: 'تقديم شكوى - شمول',
        description: 'نحن نهتم برأيك. قدم شكواك وسنعمل على حلها في أسرع وقت ممكن.',
        keywords: 'شكاوى, ملاحظات, تحسين الخدمة',
        locale: 'ar_SA'
      },
      '/ar/policies': {
        title: 'سياسة الخصوصية - شمول',
        description: 'تعرف على سياسة الخصوصية وكيفية حماية بياناتك في شمول.',
        keywords: 'سياسة الخصوصية, حماية البيانات, شمول',
        locale: 'ar_SA'
      },
      '/ar/termsandconditions': {
        title: 'الشروط والأحكام - شمول',
        description: 'شروط استخدام خدمات وأنظمة شمول.',
        keywords: 'الشروط والأحكام, اتفاقية الاستخدام',
        locale: 'ar_SA'
      },
      '/ar/suggestions': {
        title: 'الاقتراحات - شمول',
        description: 'شاركنا اقتراحاتك لتطوير أنظمة وخدمات شمول.',
        keywords: 'اقتراحات, تطوير, شاركنا رأيك',
        locale: 'ar_SA'
      },
      // Other Pages - English
      '/en/blog': {
        title: 'Shumoul Blog - Sales and Accounting Tech Articles',
        description: 'Read the latest articles and tips on business management, accounting, and modern technologies in Shumoul.',
        keywords: 'shumoul blog, accounting tips, business management, tech articles',
        locale: 'en_US'
      },
      '/en/news': {
        title: 'Shumoul News - Latest Developments and Updates',
        description: 'Follow the latest news from Shumoul, new updates, and events we participate in.',
        keywords: 'shumoul news, shumoul updates, events',
        locale: 'en_US'
      },
      '/en/commonquestions': {
        title: 'Common Questions - Shumoul',
        description: 'Answers to the most common questions about Shumoul systems and how to use them.',
        keywords: 'common questions, FAQ, shumoul help',
        locale: 'en_US'
      },
      '/en/zatcafatoora': {
        title: 'Electronic Invoicing - Shumoul and ZATCA',
        description: 'Learn about Shumoul approved electronic invoicing solutions compliant with ZATCA requirements.',
        keywords: 'electronic invoicing, ZATCA, billing, shumoul',
        locale: 'en_US'
      },
      '/en/technicalsupport': {
        title: 'Technical Support - Shumoul',
        description: 'Shumoul technical support team is ready to help you solve any technical issues you face.',
        keywords: 'technical support, tech help, contact us',
        locale: 'en_US'
      },
      '/en/kds': {
        title: 'Kitchen Display System KDS - Shumoul',
        description: 'Advanced Kitchen Display System to organize orders and improve preparation speed in restaurants.',
        keywords: 'KDS, kitchen screen, restaurant system, shumoul',
        locale: 'en_US'
      },
      '/en/support': {
        title: 'Help and Support Center - Shumoul',
        description: 'Comprehensive help center for Shumoul systems, tutorials and integrated technical support.',
        keywords: 'support, help center, shumoul support',
        locale: 'en_US'
      },
      '/en/requestatrialversion': {
        title: 'Request a Trial Version - Shumoul',
        description: 'Try Shumoul systems for free and discover how we can help you develop your business.',
        keywords: 'trial version, free trial, shumoul trial',
        locale: 'en_US'
      },
      '/en/youtubevideos': {
        title: 'Video Tutorials - Shumoul',
        description: 'Detailed video tutorials on how to use and customize different Shumoul systems.',
        keywords: 'video tutorials, shumoul guide, shumoul youtube',
        locale: 'en_US'
      },
      '/en/trainingcenter': {
        title: 'Training Center - Shumoul',
        description: 'Integrated training programs to help you and your employees master the use of Shumoul systems.',
        keywords: 'shumoul training, training center, accounting courses',
        locale: 'en_US'
      },
      '/en/complaints': {
        title: 'Submit a Complaint - Shumoul',
        description: 'We care about your opinion. Submit your complaint and we will work to solve it as soon as possible.',
        keywords: 'complaints, feedback, service improvement',
        locale: 'en_US'
      },
      '/en/policies': {
        title: 'Privacy Policy - Shumoul',
        description: 'Learn about the privacy policy and how your data is protected in Shumoul.',
        keywords: 'privacy policy, data protection, shumoul',
        locale: 'en_US'
      },
      '/en/termsandconditions': {
        title: 'Terms and Conditions - Shumoul',
        description: 'Terms of use for Shumoul services and systems.',
        keywords: 'terms and conditions, user agreement',
        locale: 'en_US'
      },
      '/en/suggestions': {
        title: 'Suggestions - Shumoul',
        description: 'Share your suggestions to develop Shumoul systems and services.',
        keywords: 'suggestions, development, share your opinion',
        locale: 'en_US'
      }
    };

    const defaultLang = route.startsWith('/en') ? 'en' : 'ar';
    return routeData[route] || {
      title: defaultLang === 'ar' ? 'شمول - حلول إدارة الأعمال السحابية' : 'Shumoul - Cloud Business Management Solutions',
      description: defaultLang === 'ar' 
        ? 'شمول يوفر حلول إدارة أعمال سحابية شاملة' 
        : 'Shumoul provides comprehensive cloud-based business management solutions',
      locale: defaultLang === 'ar' ? 'ar_SA' : 'en_US'
    };
  }
}
